import React, { useMemo } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { orderBy } from "lodash";

import PlanDetails from "../PlanDetails";
import TaskBreakdown from "../TaskBreakdown";
import Tickets from "../Tickets";
import CallBreakdown from "../CallBreakdown";
import ITHealthScore from "../ITHealthScore";
import { ITManagedDevicesScore } from "../ITManagedDevices";
import GaugeChart from "../../../common/GaugeChart";

import { groupByIntoArray } from "../../../../helpers";

const TabItem = ({
  contract,
  projects,
  tickets,
  calls,
  isMobile,
  startMonth,
  startYear,
  endMonth,
  endYear
}) => {
  const averageAssignmenttTime = useMemo(() => {
    const durations = tickets
      .filter((sp) => sp.assigned_to)
      .map((sp) => {
        const duration = moment.duration(
          moment(sp.assigned_on).diff(moment(sp.created_on))
        );
        return (
          (parseInt(duration.asMinutes(), 10) % 60) +
          parseInt(duration.asHours(), 10) * 60
        );
      });

    const totalMinutes = durations.reduce((acc, curr) => curr + acc, 0);

    const avgAssignmentTimeInMinutes = totalMinutes / durations?.length || 0;

    const avgAssignmentTimeHours = Math.floor(avgAssignmentTimeInMinutes / 60);

    const avgAssignmentTimeMinutes = Math.ceil(avgAssignmentTimeInMinutes % 60);

    if (avgAssignmentTimeHours > 0) {
      return `${avgAssignmentTimeHours}h ${avgAssignmentTimeMinutes}m`;
    }
    return `${avgAssignmentTimeMinutes}m`;
  }, [tickets]);

  const averageResolutionTime = useMemo(() => {
    const durations = tickets
      .filter((sp) => sp.assigned_to)
      .map((sp) => ({
        hours: sp.totalBillableHours.hours,
        minutes: sp.totalBillableHours.minutes
      }));

    const avgHours =
      Math.ceil(
        durations.reduce((total, next) => total + next.hours, 0) /
          durations.length
      ) || 0;

    const avgMinutes =
      Math.ceil(
        durations.reduce((total, next) => total + next.minutes, 0) /
          durations.length
      ) || 0;
    if (avgHours) {
      return `${avgHours}h ${avgMinutes}m`;
    }
    return `${avgMinutes}m`;
  }, [tickets]);

  const averageTimeInProcess = useMemo(() => {
    const durations = tickets
      .filter((sp) => sp.timeInProcess)
      .map((sp) => ({
        minutes: sp.timeInProcess
      }));

    const avgMinutes =
      Math.ceil(
        durations.reduce((total, next) => total + next.minutes, 0) /
          durations.length
      ) || 0;

    const avgTimeInProcessHours = Math.floor(avgMinutes / 60);
    const avgTimeInProcessMinutes = Math.ceil(avgMinutes % 60);
    if (avgTimeInProcessHours > 0) {
      return `${avgTimeInProcessHours}h ${avgTimeInProcessMinutes}m`;
    }
    return `${avgTimeInProcessMinutes}m`;
  }, [tickets]);

  const percentResolvedOnFirstResponse = useMemo(() => {
    const resolvedOnFirst = tickets
      .filter((sp) => sp.request_status?.label === "Resolved")
      .filter((sp) => sp.resolvedOnFirst);

    const percent = Math.ceil(
      (resolvedOnFirst?.length / tickets?.length) * 100
    );

    return Number.isNaN(percent) ? "N/A" : `${percent}%`;
  }, [tickets]);

  const projectManagers = useMemo(
    () => [
      ...new Set(projects.map((project) => project.project_manager?.full_name))
    ],
    [projects]
  );

  const resources = useMemo(() => {
    const resources1 = [
      ...new Set(
        projects
          .map((project) => project.resource1 && project.resource1.full_name)
          .filter((res) => res)
      )
    ];
    const resources2 = [
      ...new Set(
        projects
          .map((project) => project.resource2 && project.resource2.full_name)
          .filter((res) => res)
      )
    ];
    return [...new Set([...resources1, ...resources2])];
  }, [projects]);

  const tasks = useMemo(
    () =>
      calls
        .map((call) =>
          call.tasks.map((task) => ({
            ...call.service.tasks.find((st) => st.id === task.service_task),
            id: task.service_task,
            duration: task.actual_duration,
            durationUnits: task.duration_units
          }))
        )
        .filter((arr) => arr.length)
        .flat(1),
    [calls]
  );

  const groupedTasks = useMemo(
    () => groupByIntoArray(tasks, (task) => [task.id]),
    [tasks]
  );

  const totalDuration = useMemo(() => {
    let duration = 0;
    groupedTasks.forEach((task) => {
      let minutes = 0;
      task.forEach((t) => {
        if (t.durationUnits === "Hours") {
          minutes += t.duration * 60;
        } else {
          minutes += t.duration;
        }
      });
      const totalHours = Math.floor(minutes / 60);
      const totalMinutes = minutes % 60;
      const totalSeconds = totalHours * 3600 + totalMinutes * 60;
      task.totalHours = totalHours;
      task.totalMinutes = totalMinutes;
      task.totalSeconds = totalSeconds;

      duration += totalSeconds;
    });
    return duration;
  }, [groupedTasks]);

  const longestDuration = useMemo(
    () => Math.max(...groupedTasks.map((group) => group.totalSeconds)),
    [groupedTasks]
  );

  const openTickets = useMemo(
    () =>
      tickets?.filter(
        (ticket) =>
          ticket?.request_status?.label === "In Progress" ||
          ticket?.request_status?.label === "Open"
      )?.length || 0,
    [tickets]
  );

  const backloggedTickets = useMemo(
    () =>
      tickets?.filter((ticket) =>
        moment(ticket?.assignedOn).isAfter(ticket?.response_due)
      )?.length || 0,
    [tickets]
  );

  const healthScoreData = useMemo(() => {
    let filteredResult = contract.it_health_scores?.filter((el) => {
      const currentMonth = parseInt(el.month, 10);
      const currentYear = parseInt(el.year, 10);
      return (
        currentYear >= parseInt(startYear, 10) &&
        currentYear <= parseInt(endYear, 10) &&
        currentMonth >= parseInt(startMonth, 10) &&
        currentMonth <= parseInt(endMonth, 10)
      );
    });

    if (!filteredResult.length) {
      const mostRecent =
        contract.it_health_scores?.reduce((acc, curr) => {
          if (parseInt(curr.year) > parseInt(acc.year)) {
            return curr;
          }
          if (
            parseInt(curr.year) === parseInt(acc.year) &&
            curr.month > acc.month
          ) {
            return curr;
          }
          return acc;
        }, contract.it_health_scores[0]) || {};
      filteredResult = [mostRecent];
    }

    const attributeSum = filteredResult.reduce((acc, curr) => {
      Object.keys(curr).forEach((key) => {
        acc[key] = (acc[key] ?? 0) + curr[key];
      });
      return acc;
    }, {});

    const averages = {};

    Object.keys(attributeSum).forEach((key) => {
      averages[key] = attributeSum[key] / filteredResult.length;
    });
    return averages;
  }, [contract.it_health_scores, startMonth, startYear, endMonth, endYear]);

  const averageTimeSLA = useMemo(() => {
    const contractTier = contract.tier;
    const durations = tickets
      .filter((sp) => sp.assigned_to && sp.resolutions.length)
      .map((sp) => {
        const duration = moment.duration(
          moment(sp.assigned_on).diff(moment(sp.created_on))
        );
        const hours = parseInt(duration.asHours(), 10);
        switch (sp.request_priority) {
          case "Low":
            return hours > contractTier?.low_sla ? 0 : 100;
          case "Normal":
            return hours > contractTier?.normal_sla ? 0 : 100;
          case "High":
            return hours > contractTier?.high_sla ? 0 : 100;
          default:
            return 0;
        }
      });
    const allDurations = durations.reduce((acc, curr) => acc + curr, 0);
    return allDurations / durations.length / 100;
  }, [contract, tickets]);

  return (
    <div>
      <div className="itms-tabs-container">
        <div>
          <PlanDetails
            averageAssignmenttTime={averageAssignmenttTime}
            averageResolutionTime={averageResolutionTime}
            contract={contract}
            projectManagers={projectManagers}
            totalTickets={tickets.length}
            resources={resources}
            tasksCount={groupedTasks.length}
          />
          {groupedTasks.length > 15 && (
            <ITHealthScore contract={healthScoreData} />
          )}
          <div className="content-wrapper">
            <h2>Service Desk Breakdown</h2>
            <div className="ticket-details-grid">
              <div className="ticket-grid-col">
                <div>
                  <div className="leyend">Client Raised Ticket(s)</div>
                  <div className="amount">{tickets.length}</div>
                </div>
                <div>
                  <div className="leyend">Open Ticket(s)</div>
                  <div className="amount">{openTickets}</div>
                </div>
                <div>
                  <div className="leyend">Backlogged Ticket(s)</div>
                  <div className="amount">{backloggedTickets}</div>
                </div>
                <div>
                  <div className="leyend">Maintenance Alerts</div>
                  <div className="amount">
                    {healthScoreData.maintenance_alerts_resolved || 0}
                  </div>
                </div>
              </div>

              <div className="ticket-grid-col">
                <div>
                  <div className="leyend">Average Response Time</div>
                  <div className="amount">{averageAssignmenttTime}</div>
                </div>
                <div>
                  <div className="leyend">Average Resolution Time</div>
                  <div className="amount">{averageResolutionTime}</div>
                </div>
                <div>
                  <div className="leyend">Average Ticket Life</div>
                  <div className="amount">{averageTimeInProcess}</div>
                </div>
                <div>
                  <div className="leyend">% Resolved on First Response</div>
                  <div className="amount">
                    {!percentResolvedOnFirstResponse
                      ? "N/A"
                      : `${percentResolvedOnFirstResponse}`}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="content-wrapper  managed-devices">
            <ITManagedDevicesScore contract={healthScoreData} />
          </div>
        </div>
        <div>
          <div className="content-wrapper">
            <h2>Average SLA Time Met</h2>
            <div className="average-sla-container">
              <GaugeChart percent={averageTimeSLA || 0} />
            </div>
          </div>
          <TaskBreakdown
            tasksCount={tasks.length}
            tasks={orderBy(groupedTasks, "totalSeconds", "desc")}
            longestDuration={longestDuration}
            totalDuration={totalDuration}
          />
          {groupedTasks.length <= 15 && (
            <ITHealthScore contract={healthScoreData} />
          )}
        </div>
      </div>
      <Tickets tickets={tickets} isMobile={isMobile} />
      <CallBreakdown calls={calls} isMobile={isMobile} />
    </div>
  );
};

TabItem.propTypes = {
  contract: PropTypes.object,
  projects: PropTypes.array,
  tickets: PropTypes.array,
  calls: PropTypes.array,
  isMobile: PropTypes.bool,
  startMonth: PropTypes.string,
  startYear: PropTypes.string,
  endMonth: PropTypes.string,
  endYear: PropTypes.string
};

TabItem.defaultProps = {
  projects: [],
  tickets: [],
  calls: [],
  isMobile: false,
  startMonth: "0",
  startYear: "0",
  endMonth: "0",
  endYear: "0"
};

export default TabItem;
